import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

interface Props {
  phone: string;
  open: boolean;
  onClose: () => void;
}

export function SaveContactModal({ phone, open, onClose }: Props): JSX.Element | null {
  const qc = useQueryClient();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  function handleClose() {
    setFirstName('');
    setLastName('');
    setCompany('');
    setEmail('');
    setError(null);
    setSaving(false);
    onClose();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await api.post('/leads', { firstName, lastName, phone, company: company || undefined, email: email || undefined });
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['calls'] });
      handleClose();
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      setError(code === 'INVALID_PHONE' ? 'Invalid phone number.' : 'Failed to save contact.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink-900/40 p-4">
      <div className="card w-full max-w-sm p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Save as Contact</h2>
          <button type="button" onClick={handleClose} className="text-ink-400 hover:text-ink-700 text-lg leading-none">&times;</button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1">Phone</label>
            <input className="input w-full" value={phone} readOnly />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">First name *</label>
              <input
                className="input w-full"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">Last name *</label>
              <input
                className="input w-full"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1">Company</label>
            <input className="input w-full" value={company} onChange={(e) => setCompany(e.target.value)} />
          </div>

          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1">Email</label>
            <input className="input w-full" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={handleClose} className="btn-secondary text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary text-sm">
              {saving ? 'Saving…' : 'Save Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
